import {
  ChordType,
  Component,
  ComponentDesign,
  isProfileTruss,
  Member,
  MemberType,
  ProfileLineType,
  WebbingInfo,
  WebType,
} from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';
import { ExplicitCalculatedLine, findAndCalculateLine } from '@paragon/relative-geometry';
import { calculateMemberPolygon } from '@paragon/three-shared';

import { ProfileClass } from '../../library/profiles/ProfileClass';
import type { AppDispatch, RootState } from '../../store';
import { deleteItems } from '../../store/actions/deletionActions';
import { rewebTruss, startRewebTruss } from '../../store/actions/editComponentActions';
import { setMemberTypes } from '../../store/actions/editMemberActions';
import { cancelInsertion, finishInsertingNonBraceMembers, startInsertMember } from '../../store/actions/insertActions';
import {
  setRewebbingProfileSettingsWithUpdatedJoints,
  updateInsertingProfileTrussSettings,
} from '../../store/actions/profileActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getInsertingProfileTrussSettingsOrNull } from '../../store/selectors/profileSelectors';
import {
  clearRewebbingInfo,
  createWebSegmentRewebbing,
  deleteWebSegmentsRewebbing,
} from '../../store/slices/applicationSlice';
import { setComponent } from '../../store/slices/componentDesignsSlice';
import { setTrussEditsPanel } from '../../store/slices/panelDataSlice';
import {
  describeMemberGeometry,
  NO_PROFILE_ALTERNATIVE,
  openTruss,
  requireString,
  summarizeTruss,
  waitForAppToSettle,
  waitForRewebToStart,
} from './helpers';
import {
  buildProfileFromGeometry,
  describeProfileGeometry,
  PROFILE_GEOMETRY_SCHEMA,
  ProfileGeometry,
  readProfileGeometry,
  setChordPanelPoints,
} from './profileGeometry';
import { regenerateFromSettings } from './trussTools';
import { defineTool, ToolError } from './types';

const TRUSS_SCHEMA = { type: 'string', description: 'The truss’s name.' };

/**
 * How far, in inches, a web end given by the model may be from the panel point it means. Drawings dimension the ends
 * at the truss's ends, but the app puts those panel points inside the end verticals, 3.5 inches in.
 */
const JOINT_TOLERANCE = 6;

const round = (value: number) => Math.round(value * 16) / 16;

/**
 * Replaces the profile of the truss being inserted or regenerated with one built from `geometry`, failing with the
 * app’s own reason if the profile isn’t valid.
 */
export async function applyProfileGeometry(
  dispatch: AppDispatch,
  getState: () => RootState,
  geometry: ProfileGeometry,
) {
  const settings = getInsertingProfileTrussSettingsOrNull(getState());
  if (settings == null) throw new ToolError('Could not open this truss’s settings.');

  const profile = buildProfileFromGeometry(settings.profile, geometry);
  const xs = profile.profileJoints.map((joint) => joint.location.x);
  const updated = { ...settings, profile, span: Math.max(...xs) - Math.min(...xs) };
  const validated = ProfileClass.create(updated);
  if (validated.type === 'Err') throw new ToolError(`The app can’t build that outline: ${validated.err}`);

  await dispatch(updateInsertingProfileTrussSettings(updated));
  await waitForAppToSettle(getState);
}

interface PanelPoints {
  readonly TopChord?: readonly number[];
  readonly BottomChord?: readonly number[];
}

/**
 * The chord panel points set exactly on a truss, such as by set_truss_profile. Chords whose panel points the app
 * spaces itself are left out.
 */
function getFixedPanelPoints(componentDesign: ComponentDesign): PanelPoints {
  const profile = componentDesign.profile;
  if (profile == null) return {};
  const geometry = describeProfileGeometry(profile);
  const isFixed = (lineType: ProfileLineType) =>
    profile.profileLines.some((line) => line.profileLineType === lineType && line.fixedJoints.length > 0);
  return {
    ...(isFixed('TopChord') ? { TopChord: geometry.topChordPanelPoints } : {}),
    ...(isFixed('BottomChord') ? { BottomChord: geometry.bottomChordPanelPoints } : {}),
  };
}

/**
 * Runs `work` in Re-web mode and leaves it afterward, keeping what the re-web made. Re-web starts from a profile the
 * app rebuilds from the truss's members, which spaces panel points evenly, so the given panel points are put back
 * first.
 */
async function inRewebMode<T>(
  dispatch: AppDispatch,
  getState: () => RootState,
  panelPoints: PanelPoints,
  work: (webbing: () => WebbingInfo) => Promise<T>,
) {
  // Starting Re-web re-webs the truss and leaving it keeps that, so work that fails puts the truss back.
  const before = getActiveComponentDesign(getState());
  await dispatch(startRewebTruss());
  await waitForRewebToStart(getState);
  try {
    const settings = getState().application.rewebbingInfo?.profileSettings;
    if (settings == null) throw new ToolError('Could not start re-webbing this truss.');
    let profile = settings.profile;
    for (const lineType of ['TopChord', 'BottomChord'] as const) {
      const xs = panelPoints[lineType];
      if (xs != null) profile = setChordPanelPoints(profile, lineType, xs);
    }
    if (profile !== settings.profile) {
      dispatch(setRewebbingProfileSettingsWithUpdatedJoints({ ...settings, profile }));
      await dispatch(rewebTruss());
      await waitForAppToSettle(getState);
    }

    return await work(() => {
      const webbingInfo = getState().application.rewebbingInfo?.webbingInfo;
      if (webbingInfo == null) throw new ToolError('Could not start re-webbing this truss.');
      return webbingInfo;
    });
  } catch (error) {
    dispatch(clearRewebbingInfo());
    if (before != null && getState().componentDesigns.data[before.guid]?.component !== before.component) {
      dispatch(setComponent({ componentDesignGuid: before.guid, component: before.component }));
      await waitForAppToSettle(getState);
    }
    throw error;
  } finally {
    dispatch(clearRewebbingInfo());
    dispatch(setTrussEditsPanel(null));
  }
}

type WebEnd = { chord: ChordType; x: number };

const isKWeb = (webType: WebType) => webType === 'KWeb' || webType === 'KWebAndHeelWeb';

/** The webs of each webbing region, as chord-and-x ends the model can read and give back. */
function describeWebbing(webbingInfo: WebbingInfo) {
  return webbingInfo.webbingRegions.map((region, index) => {
    const joints = new Map(region.calculatedWebJoints.map((joint) => [joint.guid, joint]));
    const end = (guid: string) => {
      const joint = joints.get(guid);
      return joint == null ? null : { chord: joint.chordType, x: round(joint.location.x), y: round(joint.location.y) };
    };
    return {
      region: index + 1,
      panelPoints: region.calculatedWebJoints
        .map((joint) => ({ chord: joint.chordType, x: round(joint.location.x), y: round(joint.location.y) }))
        .sort((a, b) => a.chord.localeCompare(b.chord) || a.x - b.x),
      webs: region.webSegments.map((segment) => ({
        from: end(segment.baseJointGuid),
        to: end(segment.endJointGuid),
        type: segment.webType,
      })),
    };
  });
}

function findJoint(webbingInfo: WebbingInfo, regionIndex: number, end: WebEnd) {
  const region = webbingInfo.webbingRegions[regionIndex];
  let nearest: { guid: string; distance: number } | null = null;
  for (const joint of region.calculatedWebJoints) {
    if (joint.chordType !== end.chord) continue;
    const distance = Math.abs(joint.location.x - end.x);
    if (nearest == null || distance < nearest.distance) nearest = { guid: joint.guid, distance };
  }
  return nearest != null && nearest.distance <= JOINT_TOLERANCE ? nearest.guid : null;
}

function readWebEnd(value: unknown, what: string): WebEnd {
  const end = value as Partial<WebEnd> | null;
  if (end == null || (end.chord !== 'Top' && end.chord !== 'Bottom') || typeof end.x !== 'number')
    throw new ToolError(`${what} must be {"chord": "Top" or "Bottom", "x": inches}.`);
  return { chord: end.chord, x: end.x };
}

const WEB_END_SCHEMA = {
  type: 'object',
  properties: { chord: { type: 'string', enum: ['Top', 'Bottom'] }, x: { type: 'number' } },
  required: ['chord', 'x'],
};

/** Every point where two of a truss's reference lines cross, which is what a designer can click to draw a member. */
function getReferenceLineIntersections(component: Component) {
  const lines = (component.referenceLines ?? []).flatMap((line) => {
    const calculated = findAndCalculateLine(line.guid, component);
    return calculated == null ? [] : [{ guid: line.guid, calculated }];
  });

  const intersections: { point: { x: number; y: number }; line1Guid: string; line2Guid: string }[] = [];
  lines.forEach((line1, index) => {
    for (const line2 of lines.slice(index + 1)) {
      let point: { x: number; y: number } | null = null;
      try {
        point = line1.calculated.intersectWithLine(line2.calculated);
      } catch {
        // Parallel lines don't cross.
      }
      if (point != null) intersections.push({ point, line1Guid: line1.guid, line2Guid: line2.guid });
    }
  });
  return intersections;
}

function readPoint(value: unknown, what: string) {
  const point = value as { x?: unknown; y?: unknown } | null;
  if (point == null || typeof point.x !== 'number' || typeof point.y !== 'number')
    throw new ToolError(`${what} must be {"x": inches, "y": inches}.`);
  return { x: point.x, y: point.y };
}

/** The member types add_members can draw. */
const DRAWN_MEMBER_TYPES = ['Web', 'TopChord', 'BottomChord', 'EndVertical'] as const satisfies readonly MemberType[];
type DrawnMemberType = (typeof DRAWN_MEMBER_TYPES)[number];

const POINT_SCHEMA = {
  type: 'object',
  properties: { x: { type: 'number' }, y: { type: 'number' } },
  required: ['x', 'y'],
};

export const trussGeometryTools = [
  defineTool<{ truss: string } & Record<string, unknown>>({
    name: 'set_truss_profile',
    description:
      'Sets a profile truss’s whole shape in one step from a drawing’s dimensions: the outline’s corners, overhangs, ' +
      'bottom chord bearings (which make cantilevers when they aren’t at the ends), and the exact top and bottom ' +
      'chord panel points where webs meet the chords. Use it for monopitch, cantilevered, or any shape a create_truss ' +
      'shape can’t give exactly. Lengths are inches. Returns the shape the app built so you can compare it with the ' +
      'drawing. Plates are dropped, so run Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, ...PROFILE_GEOMETRY_SCHEMA },
      required: ['truss', 'outline'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const geometry = readProfileGeometry(input);
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!isProfileTruss(componentDesign.component))
        throw new ToolError('Only profile trusses have a profile to set.');

      await regenerateFromSettings(dispatch, getState, () => applyProfileGeometry(dispatch, getState, geometry));

      const edited = getState().componentDesigns.data[componentDesign.guid];
      if (edited == null) throw new ToolError('The truss is no longer in the project.');
      return { summary: summarizeTruss(edited) };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'get_webs',
    description:
      'Reads a truss’s webs. For a profile truss, that’s its panel points and the webs Re-web would give it, each ' +
      'end given as a chord (Top or Bottom) and x in inches from the left end, to plan set_webs; that leaves out ' +
      'members drawn with add_members or removed with delete_members. Every truss also gets its actual webs as ' +
      'built, with each one’s centerline and outline in the coordinates add_members takes; get_truss gives the same ' +
      'for every member. Reading a profile truss’s panel points goes through Re-web, which saves the truss, so ' +
      'its analysis is out of date afterward: read webs before Upgrade and Analyze, not after.',
    inputSchema: { type: 'object', properties: { truss: TRUSS_SCHEMA }, required: ['truss'] },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const { guid, component } = componentDesign;
      const built = component.members
        .filter((member) => member.memberType === 'Web')
        .map((member) => ({ name: member.name, ...describeMemberGeometry(member, component) }));
      // Floor trusses and others built from a pattern have no Re-web; their webs as built are all there is to read.
      if (!isProfileTruss(component)) return { summary: { truss: component.name, webs: built } };
      // Re-web knows nothing of an attic's room, so its panel points would describe a different truss.
      if (componentDesign.profile?.atticSettings != null)
        return {
          summary: {
            truss: component.name,
            webs: built,
            note:
              'An attic’s room isn’t part of Re-web, so set_webs would rebuild it as a plain truss. Edit its webs ' +
              'with move_joint, delete_members, and add_members instead.',
          },
        };

      // Starting Re-web re-webs the truss, and leaving it keeps that, so put the truss back after reading.
      try {
        return await inRewebMode(dispatch, getState, getFixedPanelPoints(componentDesign), async (webbing) => ({
          summary: { truss: component.name, webs: built, regions: describeWebbing(webbing()) },
        }));
      } finally {
        // Putting back an unchanged truss would still save it, which makes its analysis out of date.
        if (getState().componentDesigns.data[guid]?.component !== component) {
          dispatch(setComponent({ componentDesignGuid: guid, component }));
          await waitForAppToSettle(getState);
        }
      }
    },
  }),

  defineTool<{
    truss: string;
    webs: { from: WebEnd; to: WebEnd }[];
    keepOthers?: boolean;
    topChordPanelPoints?: number[];
    bottomChordPanelPoints?: number[];
  }>({
    name: 'set_webs',
    description:
      'Sets which panel points a profile truss’s webs connect, as dragging webs in Re-web does. List every web the ' +
      'drawing shows, each from one panel point to another (chord Top or Bottom, x in inches from the left end); ' +
      'webs not listed are removed unless keepOthers is true, except the K webs re-web makes where a chord steps, ' +
      'which always stay. Ends snap to the nearest panel point on their chord within 6 inches, ' +
      'so set the panel points first with set_truss_profile. Returns the webs the app built. Run Upgrade and Analyze ' +
      'afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        webs: {
          type: 'array',
          items: { type: 'object', properties: { from: WEB_END_SCHEMA, to: WEB_END_SCHEMA }, required: ['from', 'to'] },
        },
        keepOthers: { type: 'boolean', description: 'Keep webs that aren’t listed. Defaults to false.' },
        topChordPanelPoints: {
          ...PROFILE_GEOMETRY_SCHEMA.topChordPanelPoints,
          description:
            'Moves the top chord panel points first, as set_truss_profile does. Defaults to the truss’s own.',
        },
        bottomChordPanelPoints: {
          ...PROFILE_GEOMETRY_SCHEMA.bottomChordPanelPoints,
          description: 'As topChordPanelPoints, for the bottom chord.',
        },
      },
      required: ['truss', 'webs'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      for (const key of ['topChordPanelPoints', 'bottomChordPanelPoints'] as const) {
        const xs = input[key];
        if (xs != null && (!Array.isArray(xs) || xs.some((x) => typeof x !== 'number' || !Number.isFinite(x))))
          throw new ToolError(`"${key}" must be a list of numbers.`);
      }
      if (!Array.isArray(input.webs) || input.webs.length === 0)
        throw new ToolError('"webs" must list at least one web.');
      const wanted = input.webs.map((web, index) => ({
        from: readWebEnd(web?.from, `webs[${index}].from`),
        to: readWebEnd(web?.to, `webs[${index}].to`),
      }));

      // Re-web drops every web when one joins two points on the same chord, so those go through add_members.
      const sameChord = wanted.filter(({ from, to }) => from.chord === to.chord);
      if (sameChord.length > 0)
        throw new ToolError(
          `Re-web can’t build webs between two points on the same chord (${sameChord
            .map(({ from, to }) => `${from.chord} ${from.x} to ${to.x}`)
            .join(', ')}). Leave them out of set_webs and draw them afterward with add_members.`,
        );

      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!isProfileTruss(componentDesign.component))
        throw new ToolError(`Only profile trusses have webs to set. ${NO_PROFILE_ALTERNATIVE}`);
      if (componentDesign.profile?.atticSettings != null)
        throw new ToolError(
          'Re-web would rebuild this attic without its room. Edit its webs with move_joint, delete_members, and ' +
            'add_members instead.',
        );

      const panelPoints: PanelPoints = {
        ...getFixedPanelPoints(componentDesign),
        ...(input.topChordPanelPoints != null ? { TopChord: input.topChordPanelPoints } : {}),
        ...(input.bottomChordPanelPoints != null ? { BottomChord: input.bottomChordPanelPoints } : {}),
      };
      return inRewebMode(dispatch, getState, panelPoints, async (webbing) => {
        const info = webbing();
        const toCreate: { regionIndex: number; base: string; end: string }[] = [];
        const keep = new Set<string>();

        wanted.forEach(({ from, to }, index) => {
          const regionIndex = info.webbingRegions.findIndex(
            (_, candidate) => findJoint(info, candidate, from) != null && findJoint(info, candidate, to) != null,
          );
          if (regionIndex < 0)
            throw new ToolError(
              `webs[${index}]: there’s no panel point at ${from.chord} ${from.x} and ${to.chord} ${to.x} in one ` +
                'webbing region. Read them with get_webs, or add panel points with set_truss_profile.' +
                (info.webbingRegions.some((region) => region.calculatedWebJoints.length === 0)
                  ? ' Re-web found no panel points in part of this truss, which happens with shapes such as a ' +
                    'raised bottom chord with vertical sides; draw the webs there with add_members instead.'
                  : ''),
            );
          const base = findJoint(info, regionIndex, from)!;
          const end = findJoint(info, regionIndex, to)!;
          if (base === end) throw new ToolError(`webs[${index}] starts and ends at the same panel point.`);

          const existing = info.webbingRegions[regionIndex].webSegments.find(
            (segment) =>
              (segment.baseJointGuid === base && segment.endJointGuid === end) ||
              (segment.baseJointGuid === end && segment.endJointGuid === base),
          );
          if (existing != null) keep.add(existing.guid);
          else toCreate.push({ regionIndex, base, end });
        });

        // Re-web makes K webs where a chord steps, and can't build the region beside the step without them, so they
        // stay even when they aren't listed.
        const toDelete = input.keepOthers
          ? []
          : info.webbingRegions.flatMap((region) =>
              region.webSegments
                .filter((segment) => !keep.has(segment.guid) && !isKWeb(segment.webType))
                .map((segment) => segment.guid),
            );

        const before = getState().componentDesigns.data[componentDesign.guid]?.component;
        if (toDelete.length > 0) dispatch(deleteWebSegmentsRewebbing({ webSegmentGuids: toDelete }));
        for (const { regionIndex, base, end } of toCreate)
          dispatch(
            createWebSegmentRewebbing({ webbingRegionIndex: regionIndex, baseJointGuid: base, endJointGuid: end }),
          );
        await dispatch(rewebTruss(true));
        await waitForAppToSettle(getState);

        // Re-web drops webs it can't build, sometimes every web, without saying so. Put the truss back rather than
        // leave it missing webs, and name the ones it wouldn't build.
        const built = webbing().webbingRegions.flatMap((region) => {
          const joints = new Map(region.calculatedWebJoints.map((joint) => [joint.guid, joint]));
          return region.webSegments.flatMap((segment) => {
            const base = joints.get(segment.baseJointGuid);
            const end = joints.get(segment.endJointGuid);
            return base == null || end == null ? [] : [[base, end] as const];
          });
        });
        const isAt = (joint: { chordType: ChordType; location: { x: number } }, end: WebEnd) =>
          joint.chordType === end.chord && Math.abs(joint.location.x - end.x) <= JOINT_TOLERANCE;
        const missing = wanted.filter(
          ({ from, to }) => !built.some(([a, b]) => (isAt(a, from) && isAt(b, to)) || (isAt(a, to) && isAt(b, from))),
        );
        if (missing.length > 0) {
          if (before != null) dispatch(setComponent({ componentDesignGuid: componentDesign.guid, component: before }));
          await waitForAppToSettle(getState);
          throw new ToolError(
            missing.length === wanted.length && built.length === 0
              ? 'The app built none of the webs, so the truss was left as it was. Check that each web joins panel ' +
                  'points get_webs lists, and draw any it can’t build with add_members.'
              : `The app didn’t build ${missing
                  .map(({ from, to }) => `${from.chord} ${from.x} to ${to.chord} ${to.x}`)
                  .join(', ')}, so the truss was left as it was. Re-web can’t build some webs, such as ones between ` +
                  'two points on the same chord; leave those out and draw them afterward with add_members.',
          );
        }

        return {
          summary: {
            regions: describeWebbing(webbing()),
            truss: summarizeTruss(getState().componentDesigns.data[componentDesign.guid]),
          },
        };
      });
    },
  }),

  defineTool<{
    truss: string;
    members: { from: { x: number; y: number }; to: { x: number; y: number }; type?: DrawnMemberType }[];
  }>({
    name: 'add_members',
    description:
      'Draws members point to point, as Insert Member does, then sets their type as the properties panel does: webs ' +
      'set_webs can’t build, such as one between a low bottom chord and a raised one, or chord members to redraw, ' +
      'such as the sloped bottom chord pieces of a raised bottom chord (delete the old ones with delete_members ' +
      'first). Give each end in the truss’s coordinates (inches from the left end, up from the bottom), at the ' +
      'member’s centerline; each snaps to the nearest point where the truss’s lines cross, within 6 inches, or else the ' +
      'member runs through the given points and is cut where it meets other members. A later ' +
      're-web or regeneration removes members drawn this way, so draw them last. Returns the new members. Run ' +
      'Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        members: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              from: POINT_SCHEMA,
              to: POINT_SCHEMA,
              type: { type: 'string', enum: DRAWN_MEMBER_TYPES, description: 'Defaults to Web.' },
            },
            required: ['from', 'to'],
          },
        },
      },
      required: ['truss', 'members'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      if (!Array.isArray(input.members) || input.members.length === 0)
        throw new ToolError('"members" must list at least one member.');
      const wanted = input.members.map((member, index) => {
        const type = member?.type ?? 'Web';
        if (!DRAWN_MEMBER_TYPES.includes(type))
          throw new ToolError(`members[${index}].type must be one of ${DRAWN_MEMBER_TYPES.join(', ')}.`);
        return {
          from: readPoint(member?.from, `members[${index}].from`),
          to: readPoint(member?.to, `members[${index}].to`),
          type,
        };
      });

      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const { component } = componentDesign;
      if (component.referenceLines == null) throw new ToolError('This truss has no lines to draw a member between.');
      // Each member runs between the crossings nearest its ends, so it follows the truss's lines as it changes. Where
      // an end has no crossing nearby, such as on a bare chord, it runs on a fixed line through the given points
      // instead; either way the app cuts it where it meets the members around it. Crossings are found again before
      // each member, so one can end on a member drawn earlier in the same call.
      const locate = ({ from, to }: { from: { x: number; y: number }; to: { x: number; y: number } }) => {
        const current = getState().componentDesigns.data[componentDesign.guid]?.component ?? component;
        const intersections = getReferenceLineIntersections(current);
        const snap = (point: { x: number; y: number }) => {
          let nearest: (typeof intersections)[number] | null = null;
          let nearestDistance = Infinity;
          for (const intersection of intersections) {
            const distance = Math.hypot(intersection.point.x - point.x, intersection.point.y - point.y);
            if (distance < nearestDistance) [nearest, nearestDistance] = [intersection, distance];
          }
          return nearest != null && nearestDistance <= JOINT_TOLERANCE ? nearest : null;
        };

        const snappedFrom = snap(from);
        const snappedTo = snap(to);
        if (snappedFrom != null && snappedTo != null)
          return {
            from: snappedFrom.point,
            to: snappedTo.point,
            line: {
              lineType: 'PointToPoint',
              basePointLine1Guid: snappedFrom.line1Guid,
              basePointLine2Guid: snappedFrom.line2Guid,
              endPointLine1Guid: snappedTo.line1Guid,
              endPointLine2Guid: snappedTo.line2Guid,
              offset: 0,
            },
          };
        const length = Math.hypot(to.x - from.x, to.y - from.y);
        if (length === 0) throw new ToolError('A member’s ends must be different points.');
        return {
          from,
          to,
          line: {
            lineType: 'Absolute',
            line: { basePoint: from, direction: { x: (to.x - from.x) / length, y: (to.y - from.y) / length } },
          },
        };
      };
      const ends = wanted.map(locate);
      const members = () => getState().componentDesigns.data[componentDesign.guid]?.component.members ?? [];
      // Kept by each member's place in the request, though they're drawn in another order.
      const inserted: Member[] = [];
      const addedSoFar = () => inserted.filter((member) => member != null).map((member) => member.name);

      // Verticals first: each one closes off a panel, which gives the angled members there something to end on.
      const isVertical = ({ from, to }: (typeof ends)[number]) => Math.abs(to.x - from.x) <= 1 / 8;
      const order = ends
        .map((_, index) => index)
        .sort((a, b) => Number(isVertical(ends[b])) - Number(isVertical(ends[a])));

      // One at a time, as drawing each does: the app finds the space between members a line runs through from where
      // it was drawn, here the member's middle.
      for (const index of order) {
        ends[index] = locate(wanted[index]);
        const { from, to, line } = ends[index];
        // A member drawn through the middle of one that's already there can't be fitted; say which is in the way.
        const middle = new Point2D((from.x + to.x) / 2, (from.y + to.y) / 2);
        const current = getState().componentDesigns.data[componentDesign.guid]?.component;
        const inTheWay = current?.members.find((member) =>
          calculateMemberPolygon(member, current)?.containsPoint(middle),
        );
        if (inTheWay != null)
          throw new ToolError(
            `The member from (${from.x}, ${from.y}) to (${to.x}, ${to.y}) would run through ${inTheWay.name}, which ` +
              'is already there' +
              (addedSoFar().length > 0 ? `; the ones before it were added (${addedSoFar().join(', ')})` : '') +
              `. If ${inTheWay.name} is the same member, leave it; to redraw it, delete it first with delete_members.`,
          );

        const before = members().length;
        await dispatch(startInsertMember({ isBrace: false }));
        try {
          await dispatch(
            finishInsertingNonBraceMembers(
              [(guid: string, name: string) => ({ ...line, guid, name }) as unknown as ExplicitCalculatedLine],
              // Where the line could run through more than one space, as along a parapet, the middle picks the one
              // between its ends.
              { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 },
            ),
          );
          await waitForAppToSettle(getState);
        } finally {
          if (getState().application.insertingObjectInfo?.type === 'member') dispatch(cancelInsertion());
        }

        const member = members()[before];
        if (member == null) throw new ToolError('The app didn’t add the members. Check its notifications for why.');
        // A member the app couldn't fit between others has no cuts and no shape; take it back out.
        const cuts = member.relativeGeometry;
        if (cuts != null && !cuts.isBrace && (cuts.baseCutGuids.length === 0 || cuts.endCutGuids.length === 0)) {
          await dispatch(deleteItems([{ type: 'member', item: member }]));
          await waitForAppToSettle(getState);
          throw new ToolError(
            `The member from (${from.x}, ${from.y}) to (${to.x}, ${to.y}) doesn’t run between two members, so ` +
              'the app couldn’t fit it' +
              (addedSoFar().length > 0 ? `; the ones before it were added (${addedSoFar().join(', ')})` : '') +
              '. Each member has to end on others, and the truss has to be closed, with its end verticals in place.',
          );
        }
        inserted[index] = member;
      }

      // Insert Member always makes webs; set the others' types as the properties panel does, which renames them.
      for (const type of new Set(wanted.map((member) => member.type))) {
        if (type === 'Web') continue;
        const ofType = inserted.filter((_, index) => wanted[index].type === type);
        dispatch(setMemberTypes(componentDesign.guid, ofType, type));
      }
      await waitForAppToSettle(getState);

      const edited = getState().componentDesigns.data[componentDesign.guid];
      if (edited == null) throw new ToolError('The truss is no longer in the project.');
      const added = inserted.map(
        (member) => edited.component.members.find((candidate) => candidate.guid === member.guid) ?? member,
      );
      return {
        summary: {
          added: added.map((member, index) => ({
            name: member.name,
            type: member.memberType,
            from: { x: round(ends[index].from.x), y: round(ends[index].from.y) },
            to: { x: round(ends[index].to.x), y: round(ends[index].to.y) },
          })),
          truss: summarizeTruss(edited),
        },
      };
    },
  }),
];
